import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import {
  isDateSearchEnabled,
  isMainSearchTypeKeywords,
  isTopbarKeywordSearchEnabled,
} from '../../../../util/search';
import { DATE_MODE_QUERY_PARAM } from '../../../../util/searchDates';
import { REGION_FIELD_KEY } from '../../../../util/regions';

import { Form, LocationAutocompleteInput } from '../../../../components';

import SearchIcon from '../SearchIcon';

import IconSearchDesktop from './IconSearchDesktop';
import SearchDateSegment from './SearchDateSegment';
import SearchRegionSegment from './SearchRegionSegment';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  const inputId = isMobile ? 'keyword-search-mobile' : 'keyword-search';

  const inputField = (
    <Field
      name="keywords"
      render={({ input, meta }) => {
        return (
          <input
            className={isMobile ? css.mobileInput : css.segmentInput}
            {...input}
            id={inputId}
            data-testid={inputId}
            ref={inputRef}
            type="text"
            placeholder={intl.formatMessage({
              id: 'TopbarSearchForm.placeholder',
            })}
            autoComplete="off"
          />
        );
      }}
    />
  );

  // The sheet is one big input, so it keeps the inline submit it always had.
  if (isMobile) {
    return (
      <div className={keywordSearchWrapperClasses}>
        <button
          className={css.searchSubmit}
          aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
        >
          <div className={iconClass}>
            <IconSearchDesktop />
          </div>
        </button>
        {inputField}
      </div>
    );
  }

  return (
    <div className={css.segment}>
      <label className={css.segmentLabel} htmlFor={inputId}>
        {intl.formatMessage({ id: 'TopbarSearchForm.keywordLabel' })}
      </label>
      {inputField}
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  const submitButton = ({}) => (
    <button
      className={css.searchSubmit}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
    >
      <IconSearchDesktop />
    </button>
  );
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
            submitButton={submitButton}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const intl = useIntl();
  const { appConfig, onSubmit, ...restOfProps } = props;

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  // Null rather than undefined for an empty date: stringify in util/urlHelpers drops a null
  // param, which is how a cleared date leaves the URL instead of lingering.
  const datesFromValues = values =>
    isDateSearchEnabled(appConfig)
      ? {
          dates: values.dates || null,
          [DATE_MODE_QUERY_PARAM]: values[DATE_MODE_QUERY_PARAM] || null,
        }
      : {};

  const regionFromValues = values => ({ region: values.region || [] });

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({
        keywords: values.keywords,
        ...datesFromValues(values),
        ...regionFromValues(values),
      });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onLocationSubmit = values => {
    // Allow submit button click for an empty location search form
    if (!isMainSearchTypeKeywords(appConfig)) {
      onSubmit({
        location: values.location,
        ...datesFromValues(values),
        ...regionFromValues(values),
      });
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onLocationSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
          form,
          values,
        } = formRenderProps;
        // The pill exists whether or not Was does - Wann and Wo are reason enough for it.
        const isPill = isKeywordsSearch && !isMobile;
        const showKeywordField = isTopbarKeywordSearchEnabled();
        const hasFieldAboveSegments = isKeywordsSearch ? showKeywordField : true;
        const classes = classNames(rootClassName, className, {
          [css.mobileRoot]: isMobile,
          [css.mobileRootNoField]: isMobile && !hasFieldAboveSegments,
          [css.pill]: isPill,
        });
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        const dateSegmentMaybe = isDateSearchEnabled(appConfig) ? (
          <SearchDateSegment
            className={isMobile ? css.mobileSegment : css.desktopSegment}
            dates={values.dates}
            dateMode={values[DATE_MODE_QUERY_PARAM]}
            onChange={next => {
              form.change('dates', next.dates);
              form.change(DATE_MODE_QUERY_PARAM, next[DATE_MODE_QUERY_PARAM]);
            }}
          />
        ) : null;

        // Where has no gate: it is a control over a listing field that has always been
        // filterable, not new query behaviour. Like Wann it only sets state - the pill's own
        // submit is the single thing that searches.
        const regionSegment = (
          <SearchRegionSegment
            className={isMobile ? css.mobileSegment : css.desktopSegment}
            regions={values.region || []}
            onChange={next => form.change('region', next)}
          />
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            {isKeywordsSearch ? (
              showKeywordField ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                />
              ) : null
            ) : (
              <LocationSearchField
                desktopInputRootClass={desktopInputRootClass}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onLocationChange={onChange}
              />
            )}
            {dateSegmentMaybe}
            {regionSegment}
            {isPill ? (
              <button
                className={css.pillSubmit}
                aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
              >
                <SearchIcon rootClassName={css.pillSubmitIcon} />
              </button>
            ) : null}
            {isMobile ? (
              <button className={css.mobileSubmit}>
                {intl.formatMessage({ id: 'TopbarSearchForm.submit' })}
              </button>
            ) : null}
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
